import React, { useState, useLayoutEffect, useRef, useEffect } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  drawSelectionMarker,
  drawControlGroupLabel,
  drawGrid,
  rectContains,
  generateUnits,
  SELECTED_OWN,
  SELECTED_ENEMY,
} from "../../Utils";
import { selectPlayers } from "./gameSlice";
import {
  drawSprite,
  drawUnitShadow,
  spriteCell,
  facingOf,
  sheetFor,
  INITIAL_FACING,
  WALK_LINGER_MS,
} from "../sprites/Sprite";
import { drawTerrain, drawMapBorder } from "../terrain/Terrain";
import {
  drawMoveMarker,
  drawWaypoint,
  MOVE_MARKER_MS,
} from "../effects/MoveMarker";
import { drawHealthBar } from "../effects/HealthBar";
import {
  drawCannonball,
  drawMuzzleFlash,
  drawBlast,
  FLASH_MS,
  drawAttackReticle,
  drawTrail,
  BLAST_MS,
  MUZZLE_MS,
  TRAIL_MS,
  RETICLE_MS,
} from "../effects/Projectile";
import {
  DEFAULT_BINDINGS,
  MODIFIER_CODES,
  bindingHeld,
} from "../settings/keyBindings";
import { createPlayback } from "./playback";
import _ from "lodash";
import cannonBlue from "../../icons/bombard_cannon_blue.png";
import cannonRed from "../../icons/bombard_cannon_red.png";
import grass from "../../icons/grass_arabia.png";
import { UNIT_SIZE, UNIT_DRAW_SIZE, MAX_HP } from "../../constants/GameConstants";
// TODO: Handle units moving one after the other
// TODO: Kepp track of a turn timestamp if it is greater than 200ms
// stop the turn and start the next turn
// TODO: Estimate how many frames the slowest computer can handle
// then only do that many frames per second for everyone.
// Test the number of frames before the player gets to the game. Do this periodically

/*
 {
    extraHeaders: {
      "Access-Control-Allow-Origin": "http://localhost:3000",
      "Access-Control-Allow-Headers":
        "Origin, X-Requested-With, Content-Type, Accept",
    },
  }
  cors: {
        origin: "ws://localhost:3000",
        credentials: false,
      },
*/
const MOVE_SPEED = 5;
const GRID_SIZE_WIDTH = 800;
const GRID_SIZE_LENGTH = 800;
// Below this much mouse travel a press-and-release counts as a click on a
// single unit rather than a drag box.
const CLICK_DRAG_THRESHOLD = 5;
// How close a click has to land to count as hitting a unit.
const PICK_RADIUS = UNIT_DRAW_SIZE * 0.34;
// Re-ordering an attack onto essentially the same spot is dropped rather than
// sent: attacking is the action players spam.
const REPEAT_ORDER_PX = 14;
// A position change larger than this is a teleport (reset), not a step.
const SNAP_DISTANCE = 60;
// "No snapshot yet". A sentinel rather than null because this component is
// plain JS and TypeScript infers a prop's type from its default — a null
// default types the prop as null and refuses the number the caller passes.
// Server ticks start at zero and only climb, so a negative is unambiguous.
const NO_TICK = -1;

// A stable per-unit offset into the idle cycle, so units don't all breathe on
// the same beat. Derived from the ids, not random, so it doesn't change frame
// to frame.
const idlePhase = (playerId, unitId) =>
  (Number(playerId) * 397 + Number(unitId) * 173) % 900;

// Which control group a key press refers to, or null if it isn't one. Read off
// e.code rather than e.key: with shift held a US layout reports "!" for the 1
// key, so keying on e.key would make cmd + shift + 1 unrecognisable.
const controlGroupSlot = (e) => {
  const fromCode = /^Digit([0-9])$/.exec(e.code || "");
  if (fromCode) {
    return fromCode[1];
  }
  // Layouts and browsers that don't report a code still give a bare digit when
  // shift isn't involved, which covers recalling a group.
  return /^[0-9]$/.test(e.key) ? e.key : null;
};

// Keys pressed into a text field are typing, not commands.
const isTyping = (target) =>
  target instanceof window.HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
const Game = ({
  emitInput,
  emitAttack,
  registerCombat,
  userPlayerId,
  canvasRef,
  // The home screen renders this same component over the attract battle. That
  // battle belongs to nobody, so the mouse has to be inert there — otherwise
  // every visitor with the page open would be shoving the same units around.
  interactive = true,
  // Which chord gives which order. Defaults are here rather than only in the
  // caller so the attract battle and the tests can mount this on its own.
  bindings = DEFAULT_BINDINGS,
  // True while the controls editor is recording a chord. The keys being pressed
  // are being *named*, not given as orders, so the game has to stand down.
  keysCaptured = false,
  // The server tick the newest snapshot was produced on. Playback is timed off
  // this rather than off arrival times, which jitter.
  serverTick = NO_TICK,
  // Told whenever the selection changes, so the HUD outside the canvas can
  // show what is selected. Selection itself stays in a ref — this is a copy
  // for React, not the source of truth.
  //
  // The parameter is named in the default even though it goes unused: as with
  // NO_TICK above, the prop's type is inferred from its default, and a bare
  // `() => {}` types this as taking nothing and rejects every real handler.
  onSelectionChange = (selection) => {},
}) => {
  const players = useAppSelector(selectPlayers);
  const selectedNewCoords = useRef({ x: null, y: null });
  const requestRef = useRef();
  const previousTimeRef = useRef();
  const selectedAreaStart = useRef({ x: null, y: null });
  const selectedAreaEnd = useRef({ x: null, y: null });
  const selectedAreaReset = useRef(true); //TODO: FIX THIS true means ready to create new selected. false means you are starting to select. null means you have a selection.
  const unitsSelected = useRef(new Set());
  // Which side the selected unit ids belong to, or null when nothing is
  // selected. A selection is always one side's: box-select takes only your
  // own, and clicking an enemy takes exactly that one. Without this the ids
  // alone would be ambiguous, since both sides number their units from zero.
  const selectionOwner = useRef(null);
  // Control groups, keyed by digit. Each holds unit ids rather than units, so a
  // group keeps working as its units move, and a group whose units have all
  // died simply recalls nothing.
  const controlGroups = useRef({});
  // Set when a game starts and cleared once a unit has been picked for you, so
  // the default selection happens once per game rather than every snapshot.
  const needsDefaultSelection = useRef(false);
  // True while a left press that began with shift held is still in progress.
  const pressWasOrder = useRef(false);
  // Ordinary keys currently held, by code. A mouse event reports the modifiers
  // but nothing else, so a binding on a letter has to be tracked here across
  // key down and key up to be readable when the click arrives.
  const heldKeys = useRef(new Set());
  // True between mouse down and mouse up while dragging a selection box.
  const isDragging = useRef(false);
  // The most recent move order's target, animated then dropped.
  const moveMarker = useRef(null);
  const attackMarker = useRef(null);
  // Shots and blasts in flight. Transient effects, so they live in a ref and
  // drop themselves when finished rather than going through redux.
  const shots = useRef([]);
  const blasts = useRef([]);
  // Target of each unit's *still pending* attack order, so re-clicking the spot
  // it is already shelling never becomes a packet. The server filters the same
  // way, but this saves the round trip. Entries are dropped when the order is
  // spent — the unit fires, or a move cancels it — exactly as the server drops
  // its copy, so the same spot can be ordered again once the shot has gone off.
  const lastAttackOrder = useRef({});
  // Latest snapshot, read by the render loop without re-subscribing it.
  const playersRef = useRef(players);
  // Turns the uneven stream of snapshots into even motion. Held in a ref: only
  // the render loop reads it, once a frame, and it must not survive a re-render
  // as anything but itself.
  const playback = useRef(null);
  if (playback.current === null) {
    playback.current = createPlayback();
  }
  // The render loop is started once, so it closes over the first render's
  // props forever. Anything it reads that can change — like which player you
  // are controlling — has to come through a ref, or it stays frozen at its
  // initial value while the event handlers see the current one.
  const userPlayerIdRef = useRef(userPlayerId);
  // Per-unit render state: which way it faces and when it last actually moved.
  // Keyed by "playerId:unitId" and kept out of React state so updating it
  // during a frame doesn't trigger a re-render.
  const motion = useRef({});
  // Images are created once and tracked by ref: an image that is still loading
  // is simply skipped for that frame rather than re-triggering a render.
  const images = useRef(null);
  const grassPattern = useRef(null);
  if (images.current === null) {
    images.current = {};
    for (const src of [cannonBlue, cannonRed, grass]) {
      const element = new window.Image();
      const entry = { element, isLoaded: false };
      element.onload = () => {
        entry.isLoaded = true;
      };
      element.src = src;
      images.current[src] = entry;
    }
  }

  const spriteFor = (playerId) => images.current[sheetFor(playerId)];

  // Hand the current selection to the HUD. Called after every change rather
  // than every frame: selection changes when a player does something, which is
  // orders of magnitude rarer than a repaint.
  const publishSelection = () => {
    onSelectionChange({
      owner: unitsSelected.current.size === 0 ? null : selectionOwner.current,
      unitIds: [...unitsSelected.current],
    });
  };

  // True when the selection is yours to command. Selecting an enemy is allowed
  // so you can look at it; ordering one is not.
  const selectionIsOwn = () =>
    selectionOwner.current === userPlayerIdRef.current;

  const clearSelected = () => {
    // This will make it so the last selected area won't be redrawn.
    selectedAreaEnd.current.x = null;
    selectedAreaEnd.current.y = null;
    unitsSelected.current.clear();
    selectionOwner.current = null;
    selectedNewCoords.current = { x: null, y: null };
    publishSelection();
  };
  // Mouse events report viewport coordinates; everything the game knows about
  // lives in canvas coordinates, so convert once at the boundary.
  const canvasPoint = (e) => {
    const bounds = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
  };

  // The living unit closest to a click, within picking range, whichever side
  // it belongs to. Returns null when the click landed on empty ground.
  //
  // Either side can be picked: inspecting what you are shooting at is ordinary
  // in an RTS, and the orders below are what enforce whose units you command.
  // Dead units are skipped because they are not drawn — picking one would
  // select something invisible.
  const unitAt = (x, y) => {
    let picked = null;
    let closestDistance = PICK_RADIUS;
    for (let [playerId, unitId, player, unit] of generateUnits(players)) {
      if (unit.alive === false) {
        continue;
      }
      const distance = Math.hypot(unit.x - x, unit.y - y);
      if (distance <= closestDistance) {
        closestDistance = distance;
        picked = { playerId, unitId };
      }
    }
    return picked;
  };

  const selectSingleUnit = (x, y) => {
    unitsSelected.current.clear();
    const picked = unitAt(x, y);
    selectionOwner.current = picked === null ? null : picked.playerId;
    if (picked !== null) {
      unitsSelected.current.add(picked.unitId);
    }
    // A click isn't a drag, so there's no selection box to draw.
    selectedAreaStart.current = { x: null, y: null };
    selectedAreaEnd.current = { x: null, y: null };
    publishSelection();
  };

  // Replace the selection outright, the way recalling a control group or the
  // opening selection does. Any half-drawn box goes with it. Both callers deal
  // in your own units, so this claims the selection for you.
  const selectUnits = (unitIds) => {
    unitsSelected.current.clear();
    for (const unitId of unitIds) {
      unitsSelected.current.add(unitId);
    }
    selectionOwner.current = userPlayerIdRef.current;
    selectedAreaStart.current = { x: null, y: null };
    selectedAreaEnd.current = { x: null, y: null };
    publishSelection();
  };

  // Your own units that are still standing, in id order.
  const livingUnitIds = () => {
    const units = playersRef.current[userPlayerIdRef.current]?.units ?? {};
    return Object.keys(units).filter((unitId) => units[unitId].alive !== false);
  };

  // cmd/ctrl + shift + digit binds whatever is selected to that digit. With
  // nothing selected this does nothing at all — in particular it does not wipe
  // the group already on that key, so a mis-timed press can't cost you one.
  const assignControlGroup = (slot) => {
    // Groups hold bare unit ids and are recalled as your own, so binding an
    // enemy selection would hand you a group of units you cannot move.
    if (unitsSelected.current.size === 0 || !selectionIsOwn()) {
      return;
    }
    controlGroups.current[slot] = [...unitsSelected.current];
  };

  // The digit on its own selects the group again. Dead units are dropped as
  // they are recalled rather than when they die, so a group that loses a unit
  // still answers with the rest.
  const recallControlGroup = (slot) => {
    const group = controlGroups.current[slot];
    if (group === undefined) {
      return;
    }
    const alive = livingUnitIds();
    const survivors = group.filter((unitId) => alive.includes(unitId));
    if (survivors.length === 0) {
      return;
    }
    selectUnits(survivors);
  };

  // The key a unit answers to, for the label under it. A unit can sit in more
  // than one group; the lowest digit wins so the label doesn't flip about as
  // groups are reassigned.
  const controlGroupOf = (unitId) => {
    const slots = Object.keys(controlGroups.current)
      .filter((slot) => controlGroups.current[slot].includes(unitId))
      .sort();
    return slots.length === 0 ? null : slots[0];
  };

  // Whether the chord bound to `id` was held when `e` happened.
  const held = (id, e) => bindingHeld(bindings[id], e, heldKeys.current);

  const handleKeyDown = (e) => {
    if (!interactive || keysCaptured || isTyping(e.target)) {
      return;
    }
    // Modifiers are read off the event's flags, so only ordinary keys are
    // tracked. Codes rather than keys: with shift held a US layout reports "!"
    // for the 1 key, and a binding has to stay on the same physical key.
    if (!MODIFIER_CODES.has(e.code)) {
      heldKeys.current.add(e.code);
    }
    // A key repeat is the same press still going, and it is already tracked.
    // Acting on it would recall a control group dozens of times a second.
    if (e.repeat) {
      return;
    }
    const slot = controlGroupSlot(e);
    if (slot === null) {
      return;
    }
    // Assigning is checked first: a binding of no modifiers at all would
    // otherwise be indistinguishable from the bare digit that recalls.
    if (held("setControlGroup", e)) {
      e.preventDefault();
      assignControlGroup(slot);
      return;
    }
    // Bare digit. Anything else holding a modifier belongs to the browser.
    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      recallControlGroup(slot);
    }
  };

  // Released with a modifier down, the key up still names the same physical
  // key, so it isn't filtered the way the key down is — otherwise a key
  // released while, say, shift was tapped would stay stuck on.
  const handleKeyUp = (e) => {
    heldKeys.current.delete(e.code);
  };

  // Leaving the window swallows the key up, so a key held on the way out would
  // still read as held on the way back in.
  const handleBlur = () => {
    heldKeys.current.clear();
  };

  // Key events only reach an element that can take focus, and the canvas can't,
  // so this listens on the window instead of on the canvas.
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
    // The handlers close over the bindings, so a rebind has to re-subscribe
    // them — otherwise the game would go on answering to the old keys.
  }, [interactive, keysCaptured, bindings]);

  // Keys pressed into the editor never reach the key up handler here, so what
  // was down when it opened would otherwise still read as down afterwards.
  useEffect(() => {
    if (keysCaptured) {
      heldKeys.current.clear();
    }
  }, [keysCaptured]);

  useEffect(() => {
    registerCombat(({ shots: fired, blasts: hits }) => {
      const now = performance.now();
      for (const shot of fired) {
        shots.current.push({ ...shot, startedAt: now });
        // The server drops an order the moment it fires it, so forget it here
        // too. Without this the repeat filter has nothing to release it and
        // every later click on that same spot is swallowed for good.
        delete lastAttackOrder.current[`${shot.player}:${shot.unit}`];
        // The server's muzzle vector is authoritative, so re-aim off it even
        // if we already turned the unit when the order went out.
        aimUnit(`${shot.player}:${shot.unit}`, shot.from.x, shot.from.y,
                shot.to.x, shot.to.y);
      }
      for (const hit of hits) {
        blasts.current.push({ ...hit, startedAt: now });
      }
    });
  }, [registerCombat]);

  // Order a bombardment of a point. The reticle is local feedback only — the
  // shot itself is drawn when the server says it was fired.
  const issueAttackOrder = (point) => {
    // An enemy selection is for looking at, not commanding. Refused here as
    // well as at the server so no packet is even sent.
    if (unitsSelected.current.size === 0 || !selectionIsOwn()) {
      return;
    }
    const selected = [...unitsSelected.current];
    const fresh = selected.filter((unitId) => {
      const previous = lastAttackOrder.current[`${userPlayerId}:${unitId}`];
      return (
        !previous ||
        Math.hypot(previous.x - point.x, previous.y - point.y) > REPEAT_ORDER_PX
      );
    });
    if (fresh.length === 0) {
      return;
    }
    for (const unitId of fresh) {
      lastAttackOrder.current[`${userPlayerId}:${unitId}`] = point;
      // Turn now rather than when the shot lands: the order takes
      // COMMAND_DELAY_TICKS to come due, and a unit that pivots on the click
      // feels responsive even though the gun hasn't gone off yet.
      const unit = playersRef.current[userPlayerId]?.units[unitId];
      if (unit) {
        aimUnit(`${userPlayerId}:${unitId}`, unit.x, unit.y, point.x, point.y);
      }
    }
    emitAttack({ [userPlayerId]: fresh }, point.x, point.y);
    attackMarker.current = { x: point.x, y: point.y, startedAt: performance.now() };
  };

  // Send whatever is selected to a point. Shared by right click and by
  // shift + left click, which are two ways of giving the same order.
  // `queued` adds the point to the end of the route instead of replacing it,
  // which is what shift + right click does.
  const issueMoveOrder = (point, queued = false) => {
    // Same as attacking: an enemy selection issues nothing.
    if (!selectionIsOwn()) {
      return;
    }
    selectedNewCoords.current = point;
    if (unitsSelected.current.size > 0) {
      emitInput(
        { [userPlayerId]: [...unitsSelected.current] },
        point.x,
        point.y,
        queued
      );
      // A move cancels a pending attack server side, queued or not, so the spot
      // these units were shelling has to become orderable again here as well.
      for (const unitId of unitsSelected.current) {
        delete lastAttackOrder.current[`${userPlayerId}:${unitId}`];
      }
      // Show the target marker only when an order actually went out.
      // performance.now() shares a clock with the requestAnimationFrame stamp.
      moveMarker.current = {
        x: point.x,
        y: point.y,
        startedAt: performance.now(),
      };
    }
    // Clear the selection box; the units stay selected so you can order again.
    selectedAreaStart.current = { x: null, y: null };
    selectedAreaEnd.current = { x: null, y: null };
  };

  const handleMouseDown = (e) => {
    if (e.button === 2 || !interactive) {
      return;
    }
    e.preventDefault();
    // Fire chord held: order a bombardment rather than a move.
    if (held("fire", e)) {
      pressWasOrder.current = true;
      issueAttackOrder(canvasPoint(e));
      return;
    }
    // The move chord makes a left click an order, not the start of a selection.
    if (held("move", e)) {
      pressWasOrder.current = true;
      issueMoveOrder(canvasPoint(e));
      return;
    }
    pressWasOrder.current = false;
    clearSelected();
    const point = canvasPoint(e);
    selectedAreaStart.current.x = point.x;
    selectedAreaStart.current.y = point.y;
    selectedAreaReset.current = false;
    isDragging.current = true;
  };

  // The box is a live preview of the drag, so it has to follow the cursor
  // while the button is held. Previously the end corner was only recorded on
  // release, which meant nothing was drawn during the drag and the box then
  // appeared afterwards and stayed — exactly backwards.
  const handleMouseMove = (e) => {
    if (!isDragging.current || !interactive) {
      return;
    }
    const point = canvasPoint(e);
    selectedAreaEnd.current.x = point.x;
    selectedAreaEnd.current.y = point.y;
  };

  const handleMouseUp = (e) => {
    if (e.button === 2 || !interactive) {
      return;
    }
    e.preventDefault();
    isDragging.current = false;
    // The order already went out on mouse down. Checked with a ref rather than
    // re-reading the chord, so letting go of it mid-click can't turn the press
    // into a selection.
    if (pressWasOrder.current) {
      pressWasOrder.current = false;
      return;
    }
    // Nothing was begun on the canvas, so there is nothing to finish. This is
    // the ordinary case for mouse leave, which fires whenever the cursor
    // crosses the edge whether a button is down or not.
    //
    // Without this the null start corner is arithmetic: `point.x - null` is
    // `point.x`, so the distance below is measured from (0, 0), clears the
    // drag threshold from almost anywhere, and box-selects everything between
    // the map's origin and wherever the cursor left.
    if (
      selectedAreaStart.current.x === null ||
      selectedAreaStart.current.y === null
    ) {
      return;
    }
    const point = canvasPoint(e);
    const travelled = Math.hypot(
      point.x - selectedAreaStart.current.x,
      point.y - selectedAreaStart.current.y
    );
    selectedAreaReset.current = null;
    // Barely moved: treat it as a click on one unit rather than a tiny box.
    if (travelled < CLICK_DRAG_THRESHOLD) {
      selectSingleUnit(point.x, point.y);
      return;
    }
    selectedAreaEnd.current.x = point.x;
    selectedAreaEnd.current.y = point.y;
    setUnitsSelected();
    // Box goes as soon as the button is released; what was selected is shown
    // from here on by the rings under the units themselves.
    selectedAreaStart.current = { x: null, y: null };
    selectedAreaEnd.current = { x: null, y: null };
  };

  const handleRightClick = (e) => {
    e.preventDefault();
    if (!interactive) {
      return;
    }
    // Queue chord held: append to the route rather than replacing it, so
    // repeated clicks lay down a path walked in the order clicked.
    issueMoveOrder(canvasPoint(e), held("queueMove", e));
  };

  // Point a unit at something without moving it. facingOf() derives facing
  // from the last step taken, so a unit that fires while standing still keeps
  // whichever way it was walking and shoots over its own shoulder.
  const aimUnit = (key, fromX, fromY, toX, toY) => {
    const previous = motion.current[key] || {
      facing: INITIAL_FACING,
      lastMovedAt: -Infinity,
    };
    motion.current[key] = {
      ...previous,
      facing: facingOf(toX - fromX, toY - fromY, previous.facing),
    };
  };

  // Facing and walk state, derived from where the unit is actually *drawn*
  // rather than from the newest snapshot. With playback running behind, the
  // snapshot is the future: taking the walk cycle from it would start a unit's
  // legs moving a fifth of a second before the unit did.
  const unitMotion = (key, at, timeMs) => {
    const previous = motion.current[key] || {
      facing: INITIAL_FACING,
      lastMovedAt: -Infinity,
    };
    const dx = at.x - (previous.x ?? at.x);
    const dy = at.y - (previous.y ?? at.y);
    // Drawn positions are fractional, so this needs a threshold rather than the
    // equality test whole-pixel snapshots allowed — otherwise the last sliver
    // of a slide would read as walking.
    const hasMoved = Math.hypot(dx, dy) > 0.05;
    const current = {
      // Standing still keeps whatever way it was already pointing, which is
      // what lets aimUnit turn a stationary unit toward what it is shooting.
      facing: hasMoved ? facingOf(dx, dy, previous.facing) : previous.facing,
      lastMovedAt: hasMoved ? timeMs : previous.lastMovedAt,
      x: at.x,
      y: at.y,
    };
    motion.current[key] = current;
    return current;
  };

  // Grass fill plus the map frame. The pattern is built once, the first frame
  // after the texture finishes loading.
  const drawBackdrop = (ctx) => {
    const { width, height } = canvasRef.current;
    const turf = images.current[grass];
    if (grassPattern.current === null && turf.isLoaded) {
      grassPattern.current = ctx.createPattern(turf.element, "repeat");
    }
    if (grassPattern.current === null) {
      ctx.clearRect(0, 0, width, height);
      return;
    }
    drawTerrain(ctx, grassPattern.current, width, height);
    drawMapBorder(ctx, width, height);
  };

  // Drawn on the ground before the units, so a unit standing on the target
  // covers it rather than the other way round.
  const drawActiveMoveMarker = (ctx, timeMs) => {
    const marker = moveMarker.current;
    if (marker === null) {
      return;
    }
    const elapsed = timeMs - marker.startedAt;
    if (elapsed > MOVE_MARKER_MS) {
      moveMarker.current = null;
      return;
    }
    drawMoveMarker(ctx, marker.x, marker.y, elapsed);
  };

  // Queued paths, drawn on the ground before the units so a unit standing on a
  // waypoint covers it. Only your own routes: where the other side intends to
  // walk is not yours to see.
  const drawRoutes = (ctx) => {
    const mine = playersRef.current[userPlayerIdRef.current];
    if (mine === undefined) {
      return;
    }
    for (const unit of Object.values(mine.units)) {
      if (unit.alive === false || !unit.route) {
        continue;
      }
      for (const [x, y] of unit.route) {
        drawWaypoint(ctx, x, y);
      }
    }
  };

  // Copy this snapshot's positions into the playback buffer.
  const recordSnapshot = (tick, atMs) => {
    const positions = {};
    for (const [playerId, unitId, , unit] of generateUnits(playersRef.current)) {
      positions[`${playerId}:${unitId}`] = { x: unit.x, y: unit.y };
    }
    playback.current.record(tick, positions, atMs);
  };

  // Where a unit should be drawn *now*. The server sends ~10 discrete positions
  // a second; without this the sprite visibly teleports between them.
  const renderedPosition = (key, unit, timeMs) =>
    playback.current.positionAt(key, timeMs, { x: unit.x, y: unit.y });

  // Shots, flashes and blasts. Each drops itself once finished, so the arrays
  // stay bounded without any explicit cleanup pass.
  const drawCombat = (ctx, timeMs) => {
    shots.current = shots.current.filter((shot) => {
      const elapsed = timeMs - shot.startedAt;
      // The ball is the shortest-lived part of a shot: the muzzle smoke and the
      // trail it leaves both outlast it, so the shot is kept until the last of
      // them has faded rather than until the ball lands.
      const lifetime = Math.max(MUZZLE_MS, shot.flight_ms + TRAIL_MS);
      if (elapsed > lifetime) {
        return false;
      }
      drawTrail(ctx, shot, elapsed);
      drawMuzzleFlash(ctx, shot, elapsed);
      drawCannonball(ctx, shot, elapsed);
      return true;
    });
    blasts.current = blasts.current.filter((blast) => {
      const elapsed = timeMs - blast.startedAt;
      if (elapsed > BLAST_MS) {
        return false;
      }
      drawBlast(ctx, blast, elapsed);
      return true;
    });
  };

  const drawAttackMarker = (ctx, timeMs) => {
    const marker = attackMarker.current;
    if (marker === null) {
      return;
    }
    const elapsed = timeMs - marker.startedAt;
    if (elapsed > RETICLE_MS) {
      attackMarker.current = null;
      return;
    }
    drawAttackReticle(ctx, marker.x, marker.y, elapsed);
  };

  const drawPlayer = (ctx, timeMs) => {
    for (let [playerId, unitId, player, unit] of generateUnits(
      playersRef.current
    )) {
      const key = `${playerId}:${unitId}`;
      // Position first: the walk cycle is derived from where the sprite is
      // being drawn, so it has to be worked out before the motion is.
      const at = renderedPosition(key, unit, timeMs);
      const { facing, lastMovedAt } = unitMotion(key, at, timeMs);
      if (unit.alive === false) {
        continue;
      }
      const isSelected =
        playerId === selectionOwner.current &&
        unitsSelected.current.has(unitId);
      // Shadow first: it sits on the ground under both the marker and the body.
      drawUnitShadow(ctx, at.x, at.y);
      if (isSelected) {
        const isMine = playerId === userPlayerIdRef.current;
        drawSelectionMarker(
          ctx,
          at.x,
          at.y,
          isMine ? SELECTED_OWN : SELECTED_ENEMY
        );
        // Groups are yours alone, and an enemy unit can share an id with one of
        // yours, so an enemy selection must not wear your group's number.
        const group = isMine ? controlGroupOf(unitId) : null;
        if (group !== null) {
          drawControlGroupLabel(ctx, at.x, at.y, group);
        }
      }
      const sheet = spriteFor(playerId);
      if (sheet.isLoaded) {
        const isMoving = timeMs - lastMovedAt < WALK_LINGER_MS;
        drawSprite(
          ctx,
          sheet.element,
          at.x,
          at.y,
          spriteCell(isMoving, timeMs, facing, idlePhase(playerId, unitId))
        );
      }
      // drawn after the sprite so it is never hidden behind the unit
      if (isSelected) {
        drawHealthBar(ctx, at.x, at.y, unit.hp ?? MAX_HP, MAX_HP);
      }
    }
  };

  const getNewCoord = (currentCord, moveToCoord) => {
    const diff = currentCord - moveToCoord;
    if (diff == 0) {
      return currentCord;
    }

    if (diff !== 0) {
      // This ensures we'll never jump over the new coord bc move_speed is too big.
      if (Math.abs(diff) <= MOVE_SPEED) {
        return moveToCoord;
      } else {
        return currentCord + -1 * Math.sign(diff) * MOVE_SPEED;
      }
    }
  };

  /* const bouncePlayersTooClose = () => {
    // move players in one movement_speed away from player if too close
    players.flatMap((player, i) =>
      players.slice(i + 1).map((otherPlayer) => {
        const shouldBounce =
          player.x === otherPlayer.x && player.y === otherPlayer.y;
        if (shouldBounce) {
          const randomDirectionX = Math.random() < 0.5 ? -1 : 1;
          const randomDirectionY = Math.random() < 0.5 ? -1 : 1;
          dispatch(
            updatePlayer({
              id: player.id,
              x: player.x + UNIT_SIZE * randomDirectionX,
              y: player.y + UNIT_SIZE * randomDirectionY,
            })
          );
        }
      })
    );
    
    TODO: IDK, i'm trying to tell the bounce players stuff to stop bouncing. 
    const allSelectedPlayersInRange = players.map((player) => {
      unitsSelected.current.forEach((player) => {
        // TODO: This is looking at all players, instead of all players selected
        const withinAcceptableRange =
          _.inRange(
            player.x,
            selectedNewCoords.current.x - UNIT_SIZE,
            selectedNewCoords.current.x + UNIT_SIZE
          ) &&
          _.inRange(
            player.y,
            selectedNewCoords.current.y - UNIT_SIZE,
            selectedNewCoords.current.y + UNIT_SIZE
          );
        if (withinAcceptableRange) {
          return true;
        }
        return false;
      });
    });
    console.log(
      "here",
      allSelectedPlayersInRange.some((i) => i === false)
    );
    if (!allSelectedPlayersInRange.some((i) => i === false)) {
      selectedNewCoords.current = { x: null, y: null };
    }
  }; */

  // A drag box takes your own units only. Dragging across a mixed crowd and
  // getting a selection you can only half command would be worse than useless
  // — inspecting an enemy is what the single click is for.
  const setUnitsSelected = () => {
    selectionOwner.current = userPlayerId;
    for (let [playerId, unitId, player, unit] of generateUnits(players)) {
      if (!(playerId === userPlayerId)) {
        continue;
      }
      const isSelected = rectContains(
        selectedAreaStart.current.x,
        selectedAreaStart.current.y,
        selectedAreaEnd.current.x,
        selectedAreaEnd.current.y,
        unit.x,
        unit.y
      );
      if (isSelected) {
        unitsSelected.current.add(unitId);
      } else if (selectedAreaReset.current !== null) {
        unitsSelected.current.delete(unitId);
      }
    }
    publishSelection();
  };

  const drawSelectedArea = () => {
    const shouldNotDrawSelected = [
      selectedAreaStart.current.x,
      selectedAreaStart.current.y,
      selectedAreaEnd.current.x,
      selectedAreaEnd.current.y,
    ].some((coord) => coord === null);
    if (shouldNotDrawSelected) {
      return;
    }
    const ctx = canvasRef.current.getContext("2d");
    // Set the stroke explicitly. It used to inherit whatever style the last
    // draw happened to leave behind — in practice the map border's pale tan —
    // so the box was nearly invisible against the grass.
    ctx.save();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "rgba(238, 248, 224, 0.95)";
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.rect(
      selectedAreaStart.current.x,
      selectedAreaStart.current.y,
      selectedAreaEnd.current.x - selectedAreaStart.current.x,
      selectedAreaEnd.current.y - selectedAreaStart.current.y
    );
    ctx.stroke();
    ctx.restore();
  };

  const animate = (time) => {
    if (previousTimeRef.current !== undefined) {
      const ctx = canvasRef.current.getContext("2d");
      drawBackdrop(ctx);
      drawSelectedArea();
      //drawGrid(canvasRef.current);
      drawRoutes(ctx);
      drawActiveMoveMarker(ctx, time);
      drawAttackMarker(ctx, time);
      drawPlayer(ctx, time);
      // after the units, so a ball passing in front of one is drawn over it
      drawCombat(ctx, time);

      //bouncePlayersTooClose();
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    userPlayerIdRef.current = userPlayerId;
    // The buffered positions belong to the game just left. Interpolating out of
    // them would slide every unit from where it stood in that match to where it
    // stands in this one.
    playback.current.reset();
    motion.current = {};
    // switching sides drops the old selection; unit ids are per-player, so
    // keeping them would highlight the same-numbered unit on the new side
    unitsSelected.current.clear();
    selectionOwner.current = null;
    publishSelection();
    // control groups are ids too, so they belong to the side that made them
    controlGroups.current = {};
    // Starting a game leaves you commanding a unit already, so the first thing
    // you do can be an order rather than a click to select.
    needsDefaultSelection.current = interactive;
    // Take the units from the snapshot already in hand; if none has arrived yet
    // the next one picks this up.
    applyDefaultSelection();
  }, [userPlayerId, interactive]);

  // Runs off the snapshot rather than on mount: the units you are given only
  // exist once the server has said what they are.
  const applyDefaultSelection = () => {
    if (!needsDefaultSelection.current) {
      return;
    }
    const [first] = livingUnitIds();
    if (first === undefined) {
      return;
    }
    needsDefaultSelection.current = false;
    selectUnits([first]);
  };

  useEffect(() => {
    // Positions first: recordSnapshot copies them out of this ref.
    playersRef.current = players;
    if (serverTick !== NO_TICK) {
      recordSnapshot(serverTick, performance.now());
    }
    applyDefaultSelection();
  }, [players, serverTick]);

  // Started once. Keying this on [players] tore the loop down and rebuilt it on
  // every snapshot, which put a hitch in the animation at the network rate.
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  return (
    <>
      <canvas
        width={GRID_SIZE_WIDTH}
        height={GRID_SIZE_LENGTH}
        onContextMenu={handleRightClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        // draggable would start a native HTML5 drag on press-and-move, which
        // suppresses the mousemove events the selection box needs
        draggable={false}
        ref={canvasRef}
      />
    </>
  );
};
export default Game;
